import { useState, type MouseEvent } from 'react'
import { formatDay } from '@/lib/time'
import { cn } from '@/lib/utils'

export interface SparklineProps {
  /** Ranks left to right, oldest first. 0 = not ranked that day: the line
   *  breaks there. */
  series: number[]
  /** One date per point, for the hover label ("10. 17. #8"). Without it the
   *  line has no hover. */
  dates?: string[]
  /** Pixel width, or 'fill' to stretch across the parent. */
  width?: number | 'fill'
  height?: number
  /** Read out instead of the points. */
  label?: string
  className?: string
}

const PAD = 3

/** A rank line. A lower rank is better, so the axis is turned over (#1 on
 *  top). One colour (chart-1) whichever way it went — the direction is the
 *  Delta label's job — with a dot on the latest point. */
export function Sparkline({ series, dates, width = 96, height = 24, label, className }: SparklineProps) {
  const [hover, setHover] = useState<number | null>(null)
  const ranked = series.filter((r) => r > 0)
  const fill = width === 'fill'
  const w = fill ? 100 : width
  const box = { width: fill ? '100%' : width, height }

  if (ranked.length === 0) {
    return (
      <span style={box} className={cn('inline-flex shrink-0 items-center text-micro text-muted-foreground', className)}>
        이력 없음
      </span>
    )
  }

  const min = Math.min(...ranked)
  const max = Math.max(...ranked)
  const span = Math.max(1, max - min)
  const n = series.length
  const x = (i: number) => (n > 1 ? PAD + (i * (w - PAD * 2)) / (n - 1) : w / 2)
  const y = (r: number) => PAD + ((r - min) / span) * (height - PAD * 2)

  const segments: string[] = []
  let cur = ''
  series.forEach((r, i) => {
    if (r <= 0) {
      if (cur) segments.push(cur)
      cur = ''
      return
    }
    cur += `${cur ? 'L' : 'M'}${x(i).toFixed(2)},${y(r).toFixed(2)} `
  })
  if (cur) segments.push(cur)

  const last = n - 1
  const lastRank = series[last]
  const name = label ?? series.map((r, i) => `${dates?.[i] ? formatDay(dates[i]) : i + 1} ${r > 0 ? `#${r}` : '순위 밖'}`).join(', ')

  const onMove = (e: MouseEvent<HTMLSpanElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.width <= 0) return
    const rel = ((e.clientX - rect.left) / rect.width) * w
    const i = Math.round(((rel - PAD) / (w - PAD * 2)) * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }

  const hx = hover !== null ? (x(hover) / w) * 100 : 0
  const hr = hover !== null ? series[hover] : 0

  return (
    <span
      role="img"
      aria-label={name}
      style={box}
      className={cn('relative inline-block shrink-0 align-middle', className)}
      onMouseMove={dates ? onMove : undefined}
      onMouseLeave={dates ? () => setHover(null) : undefined}
    >
      <svg
        viewBox={`0 0 ${w} ${height}`}
        preserveAspectRatio={fill ? 'none' : undefined}
        width="100%"
        height="100%"
        className="block overflow-visible"
        aria-hidden
      >
        {segments.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            className="stroke-chart-1"
          />
        ))}
        {!fill && lastRank > 0 && <circle cx={x(last)} cy={y(lastRank)} r={2.5} className="fill-chart-1" />}
        {!fill && hover !== null && hr > 0 && <circle cx={x(hover)} cy={y(hr)} r={3} className="fill-card stroke-chart-1" strokeWidth={1.5} />}
      </svg>
      {/* Stretched, the SVG would draw circles as ovals; these are HTML. */}
      {fill && lastRank > 0 && (
        <span
          aria-hidden
          className="absolute h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-chart-1"
          style={{ left: `${(x(last) / w) * 100}%`, top: y(lastRank) }}
        />
      )}
      {hover !== null && dates && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded border border-border bg-card px-1.5 py-0.5 text-micro tabular-nums text-foreground shadow-overlay"
          style={{ left: `${hx}%` }}
        >
          {formatDay(dates[hover])} {hr > 0 ? `#${hr}` : '순위 밖'}
        </span>
      )}
    </span>
  )
}
